import React, { useRef, useCallback } from 'react';
import Draggable from 'react-draggable';
import { MoreHorizontal } from 'lucide-react';

export const FolderItem = React.memo(({
  folder,
  updateFolder,
  onOpen,
  onOpenFolderTools,
  zoom = 1,
  noteCount = 0,
}) => {
  const nodeRef = useRef(null);
  const didDragRef = useRef(false);

  const position = folder.position || { x: 120, y: 120 };

  const handleDragStart = useCallback(() => {
    didDragRef.current = false;
  }, []);

  const handleDrag = useCallback(() => {
    didDragRef.current = true;
  }, []);

  const handleStop = useCallback((e, data) => {
    if (didDragRef.current) {
      updateFolder(folder.id, { position: { x: data.x, y: data.y } });
    }
  }, [folder.id, updateFolder]);

  // Click → open the folder directly (no delay, no double-click required)
  const handleClick = useCallback(() => {
    if (didDragRef.current) return;
    onOpen(folder.id);
  }, [folder.id, onOpen]);

  const handleToolsClick = useCallback((e) => {
    e.stopPropagation();
    onOpenFolderTools(folder.id);
  }, [folder.id, onOpenFolderTools]);

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      scale={zoom}
      onStart={handleDragStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <div
        ref={nodeRef}
        className="absolute select-none"
        style={{ width: 108 }}
      >
        {/* Folder card */}
        <div
          onClick={handleClick}
          className="group relative cursor-pointer flex flex-col items-center p-2 rounded-xl hover:bg-accent/20 transition-colors"
        >
          <button
            onClick={handleToolsClick}
            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 flex items-center justify-center rounded hover:bg-accent text-muted-foreground z-10"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {/* Image / icon */}
          <div className="w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center bg-accent/20 border border-border shadow-sm hover:shadow-md transition-shadow">
            {folder.image ? (
              <img
                src={folder.image}
                alt={folder.name}
                className="w-full h-full object-cover"
                draggable={false}
                loading="lazy"
              />
            ) : (
              <span className="text-4xl pointer-events-none">📁</span>
            )}
          </div>

          {/* Title */}
          <p className="text-center text-xs font-medium mt-2 w-full truncate px-1 text-foreground">
            {folder.name}
          </p>
          <p className="text-center text-[10px] text-muted-foreground">
            {noteCount} {noteCount === 1 ? 'note' : 'notes'}
          </p>
        </div>
      </div>
    </Draggable>
  );
});

export default FolderItem;
